import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Heart, Shield, Users, Zap, TrendingUp, Award, 
  CheckCircle, ArrowRight, Building2, Stethoscope, 
  Briefcase, Code, Database, Lock, Clock, Globe,
  BarChart3, Phone, Mail, Calendar, Star, Sparkles, Cloud
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Partners = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('providers');
  const [isMobile, setIsMobile] = useState(false);
  const [isSmallMobile, setIsSmallMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsSmallMobile(window.innerWidth <= 480);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Partnership metrics
  const metrics = [
    { value: '2,500+', label: 'Healthcare Providers', icon: Stethoscope },
    { value: '150+', label: 'Partner Organizations', icon: Building2 },
    { value: '500K+', label: 'Patients Served', icon: Users },
    { value: '99.9%', label: 'Platform Uptime', icon: TrendingUp }
  ];

  // Core differentiators
  const differentiators = [
    {
      icon: Shield,
      color: '#1D57D5',
      title: 'Verified Specialist Network',
      description: 'Access to 2,500+ board-certified healthcare professionals across 40+ specializations, all thoroughly vetted and verified.'
    },
    {
      icon: Zap,
      color: '#F4CE7B',
      title: 'AI-Powered Workflows',
      description: 'Health Genie AI handles administrative tasks, appointment scheduling, and patient communication, reducing burden by 40%.'
    },
    {
      icon: Globe,
      color: '#28C08A',
      title: 'Hybrid Care Delivery',
      description: 'Seamless integration of digital consultations and in-person visits with unified patient records and communication.'
    },
    {
      icon: Lock,
      color: '#FF6B6B',
      title: 'Enterprise-Grade Security',
      description: 'HIPAA, GDPR, and ISO 27001 compliant platform with end-to-end encryption and secure data storage.'
    },
    {
      icon: TrendingUp,
      color: '#9333EA',
      title: 'Scalable Nationwide Infrastructure',
      description: 'Cloud-based platform handling 100K+ daily consultations with 99.9% uptime and real-time synchronization.'
    },
    {
      icon: BarChart3,
      color: '#F97316',
      title: 'Advanced Analytics Dashboard',
      description: 'Real-time insights on patient outcomes, provider performance, utilization rates, and operational efficiency.'
    },
    {
      icon: Award,
      color: '#E91E8C',
      title: 'Dedicated Partnership Support',
      description: '24/7 technical support, dedicated account managers, and comprehensive onboarding with training programs.'
    }
  ];

  // Partnership programs
  const programs = [
    {
      id: 'providers',
      icon: Stethoscope,
      title: 'Healthcare Providers & Clinics',
      gradient: 'linear-gradient(135deg, #1D57D5 0%, #286CFF 100%)',
      description: 'Expand your practice reach and reduce administrative burden',
      benefits: [
        'Digital profile on GEWO platform with verified badge',
        '24/7 video consultation capabilities',
        'Automated appointment scheduling & reminders',
        'Digital prescription & medical records',
        'Patient review & rating system',
        'Revenue optimization with transparent pricing'
      ],
      cta: 'Join Provider Network',
      link: '/publish-practice'
    },
    {
      id: 'hospitals',
      icon: Building2,
      title: 'Hospitals & Health Systems',
      gradient: 'linear-gradient(135deg, #28C08A 0%, #20A577 100%)',
      description: 'Transform your hospital operations with digital-first infrastructure',
      benefits: [
        'Enterprise patient management system',
        'Integrated OPD & IPD workflows',
        'Multi-specialty coordination tools',
        'Centralized medical records & imaging',
        'Bed management & resource allocation',
        'Quality metrics & compliance reporting'
      ],
      cta: 'Explore Hospital Solutions',
      link: '/hospital-partnerships'
    },
    {
      id: 'corporate',
      icon: Briefcase,
      title: 'Corporate Wellness Programs',
      gradient: 'linear-gradient(135deg, #F4CE7B 0%, #E8C068 100%)',
      description: 'Comprehensive employee health benefits with measurable ROI',
      benefits: [
        'Customized wellness plans for employees',
        'Unlimited video consultations & health checks',
        'Mental health support & counseling',
        'Chronic disease management programs',
        'Health analytics & utilization reports',
        'Flexible pricing based on employee count'
      ],
      cta: 'Get Corporate Plan',
      link: '/organizations'
    },
    {
      id: 'tech',
      icon: Code,
      title: 'API Integration Partners',
      gradient: 'linear-gradient(135deg, #9333EA 0%, #7C3AED 100%)',
      description: 'Build on GEWO platform with our healthcare APIs',
      benefits: [
        'RESTful API with comprehensive documentation',
        'Patient authentication & authorization',
        'Appointment scheduling & management',
        'Electronic health records (EHR) integration',
        'Real-time notification webhooks',
        'Sandbox environment for testing'
      ],
      cta: 'View API Documentation',
      link: '/api-partners'
    },
    {
      id: 'innovators',
      icon: Sparkles,
      title: 'Digital Health Innovators',
      gradient: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
      description: 'Collaborate on next-generation healthcare solutions',
      benefits: [
        'Access to GEWO healthcare network',
        'Co-innovation opportunities with R&D team',
        'Pilot programs with real patient data',
        'Funding & acceleration support',
        'Technical infrastructure & hosting',
        'Go-to-market partnership'
      ],
      cta: 'Partner With Us',
      link: '/innovation-partnership'
    },
    {
      id: 'ngo',
      icon: Heart,
      title: 'NGO & Public Health',
      gradient: 'linear-gradient(135deg, #E91E8C 0%, #D91C7A 100%)',
      description: 'Scale healthcare access for underserved communities',
      benefits: [
        'Subsidized platform access for NGOs',
        'Community health worker tools',
        'Multi-language support (12+ languages)',
        'Offline-first mobile capabilities',
        'Public health campaign management',
        'Impact tracking & reporting'
      ],
      cta: 'Start Community Program',
      link: '/organizations'
    }
  ];

  // Success stories
  const testimonials = [
    {
      quote: "GEWO has transformed how we deliver care. Our patient volume increased by 60% while administrative costs decreased by 35%. The AI assistant handles routine queries, allowing our staff to focus on complex cases.",
      author: "Dr. Priya Sharma",
      role: "Medical Director",
      organization: "Healthfirst Multispecialty Clinic, Mumbai",
      avatar: "PS",
      gradient: 'linear-gradient(135deg, #1D57D5 0%, #286CFF 100%)'
    },
    {
      quote: "As a corporate HR leader, GEWO's platform solved our biggest challenge—providing accessible healthcare to 5,000 employees across 12 cities. The utilization rate is 78%, and employee satisfaction scores increased by 40%.",
      author: "Rajesh Kumar",
      role: "Head of HR",
      organization: "TechCorp India",
      avatar: "RK",
      gradient: 'linear-gradient(135deg, #28C08A 0%, #20A577 100%)'
    },
    {
      quote: "Integrating GEWO's API into our health insurance platform was seamless. Their technical support team is exceptional. We now process 10,000+ teleconsultation claims monthly with 99.5% accuracy.",
      author: "Anjali Mehta",
      role: "CTO",
      organization: "SecureHealth Insurance",
      avatar: "AM",
      gradient: 'linear-gradient(135deg, #F4CE7B 0%, #E8C068 100%)'
    }
  ];

  const activeProgram = programs.find(p => p.id === activeTab) || programs[0];

  return (
    <div style={{ background: 'white', minHeight: '100vh' }}>
      <Navbar />
      
      {/* Hero Section */}
      <div style={{
        background: 'linear-gradient(135deg, #0D2C6E 0%, #1D57D5 50%, #286CFF 100%)',
        paddingTop: isMobile ? '80px' : '120px',
        paddingBottom: isMobile ? '60px' : '80px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative Elements */}
        <div style={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          width: isMobile ? '150px' : '300px',
          height: isMobile ? '150px' : '300px',
          background: 'radial-gradient(circle, rgba(244, 206, 123, 0.15) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(40px)'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '10%',
          left: '5%',
          width: isMobile ? '120px' : '250px',
          height: isMobile ? '120px' : '250px',
          background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(40px)'
        }} />

        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '0 16px' : '0 32px', position: 'relative', zIndex: 1 }}>
          {/* Badge */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(244, 206, 123, 0.15)',
            backdropFilter: 'blur(10px)',
            border: '2px solid rgba(244, 206, 123, 0.3)',
            borderRadius: '50px',
            padding: isMobile ? '8px 16px' : '10px 24px',
            marginBottom: isMobile ? '24px' : '32px'
          }}>
            <Heart size={isMobile ? 16 : 20} color="#F4CE7B" fill="#F4CE7B" />
            <span style={{ color: '#F4CE7B', fontSize: isSmallMobile ? '11px' : isMobile ? '12px' : '14px', fontWeight: '700', letterSpacing: '0.5px' }}>
              TRUSTED BY 2,500+ HEALTHCARE PARTNERS
            </span>
          </div>

          {/* Headline */}
          <h1 style={{
            fontSize: isSmallMobile ? '28px' : isMobile ? '36px' : '72px',
            fontWeight: '900',
            color: 'white',
            lineHeight: '1.1',
            marginBottom: isMobile ? '20px' : '24px',
            letterSpacing: isMobile ? '-1px' : '-2px'
          }}>
            Partner With GEWO<br />
            <span style={{
              background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Transform Healthcare Delivery
            </span>
          </h1>

          {/* Subtitle */}
          <p style={{
            fontSize: isSmallMobile ? '14px' : isMobile ? '16px' : '22px',
            color: 'rgba(255, 255, 255, 0.9)',
            lineHeight: '1.6',
            maxWidth: '700px',
            marginBottom: isMobile ? '32px' : '48px'
          }}>
            Join India's most trusted digital healthcare ecosystem. Secure, scalable, and AI-assisted care platform built for providers, hospitals, enterprises, and innovators.
          </p>

          {/* CTA Buttons */}
          <div style={{ display: 'flex', gap: isMobile ? '12px' : '16px', flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
            <button
              onClick={() => document.getElementById('partnership-programs').scrollIntoView({ behavior: 'smooth' })}
              style={{
                background: 'linear-gradient(135deg, #F4CE7B 0%, #E8C068 100%)',
                color: '#0D2C6E',
                border: 'none',
                borderRadius: '50px',
                padding: isMobile ? '16px 24px' : '18px 40px',
                fontSize: isMobile ? '16px' : '18px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                width: isMobile ? '100%' : 'auto',
                boxShadow: '0 8px 24px rgba(244, 206, 123, 0.4)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(244, 206, 123, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(244, 206, 123, 0.4)';
              }}
            >
              Become a Partner
              <ArrowRight size={isMobile ? 18 : 20} />
            </button>
            <button
              onClick={() => navigate('/organizations')}
              style={{
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '50px',
                padding: isMobile ? '16px 24px' : '18px 40px',
                fontSize: isMobile ? '16px' : '18px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                width: isMobile ? '100%' : 'auto',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                e.currentTarget.style.border = '2px solid rgba(255, 255, 255, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                e.currentTarget.style.border = '2px solid rgba(255, 255, 255, 0.3)';
              }}
            >
              Schedule a Demo
              <Calendar size={isMobile ? 18 : 20} />
            </button>
          </div>
        </div>
      </div>

      {/* Metrics Bar */}
      <div style={{
        background: 'white',
        boxShadow: '0 -4px 24px rgba(0, 0, 0, 0.08)',
        position: 'relative',
        zIndex: 2
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: isMobile ? '32px 16px' : '48px 32px',
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: isMobile ? '24px' : '32px'
        }}>
          {metrics.map((metric, index) => {
            const IconComponent = metric.icon;
            return (
              <div key={index} style={{ textAlign: 'center' }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: isMobile ? '56px' : '64px',
                  height: isMobile ? '56px' : '64px',
                  background: 'linear-gradient(135deg, #1D57D5 0%, #286CFF 100%)',
                  borderRadius: '16px',
                  marginBottom: isMobile ? '12px' : '16px',
                  boxShadow: '0 4px 16px rgba(29, 87, 213, 0.3)'
                }}>
                  <IconComponent size={isMobile ? 28 : 32} color="white" />
                </div>
                <div style={{ fontSize: isSmallMobile ? '24px' : isMobile ? '28px' : '36px', fontWeight: '900', color: '#1D57D5', marginBottom: '8px' }}>
                  {metric.value}
                </div>
                <div style={{ fontSize: isMobile ? '14px' : '16px', color: '#64748B', fontWeight: '600' }}>
                  {metric.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Why Partner With GEWO */}
      <div style={{
        background: 'linear-gradient(180deg, #F8FAFC 0%, white 100%)',
        padding: isMobile ? '60px 0' : '120px 0'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '0 16px' : '0 32px' }}>
          {/* Section Header */}
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '40px' : '64px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'linear-gradient(135deg, #E3EDFF 0%, #F0F6FF 100%)',
              border: '2px solid #1D57D5',
              borderRadius: '50px',
              padding: isMobile ? '8px 16px' : '10px 24px',
              marginBottom: isMobile ? '20px' : '24px'
            }}>
              <Award size={isMobile ? 16 : 20} color="#1D57D5" />
              <span style={{ color: '#1D57D5', fontSize: isSmallMobile ? '11px' : isMobile ? '12px' : '14px', fontWeight: '700', letterSpacing: '0.5px' }}>
                WHY CHOOSE GEWO
              </span>
            </div>
            <h2 style={{
              fontSize: isSmallMobile ? '24px' : isMobile ? '32px' : '48px',
              fontWeight: '900',
              color: '#1A202C',
              marginBottom: '20px',
              letterSpacing: '-1px'
            }}>
              Built for Healthcare Excellence
            </h2>
            <p style={{
              fontSize: isMobile ? '16px' : '20px',
              color: '#64748B',
              maxWidth: '700px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              GEWO provides the most comprehensive digital healthcare infrastructure in India, trusted by leading providers and enterprises.
            </p>
          </div>

          {/* Differentiators Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: isMobile ? '20px' : '32px'
          }}>
            {differentiators.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div
                  key={index}
                  style={{
                    background: 'white',
                    border: '2px solid #E8ECF0',
                    borderRadius: '20px',
                    padding: isMobile ? '24px' : '36px',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.12)';
                    e.currentTarget.style.borderColor = item.color;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = '#E8ECF0';
                  }}
                >
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: isMobile ? '64px' : '72px',
                    height: isMobile ? '64px' : '72px',
                    background: `linear-gradient(135deg, ${item.color}20 0%, ${item.color}10 100%)`,
                    borderRadius: '16px',
                    marginBottom: isMobile ? '20px' : '24px'
                  }}>
                    <IconComponent size={isMobile ? 32 : 36} color={item.color} />
                  </div>
                  <h3 style={{
                    fontSize: isMobile ? '18px' : '22px',
                    fontWeight: '700',
                    color: '#1A202C',
                    marginBottom: '12px'
                  }}>
                    {item.title}
                  </h3>
                  <p style={{
                    fontSize: isMobile ? '14px' : '16px',
                    color: '#64748B',
                    lineHeight: '1.7'
                  }}>
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Partnership Programs */}
      <div id="partnership-programs" style={{
        background: 'linear-gradient(180deg, white 0%, #F8FAFC 100%)',
        padding: isMobile ? '60px 0' : '120px 0'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '0 16px' : '0 32px' }}>
          {/* Section Header */}
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '40px' : '64px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'linear-gradient(135deg, #FFF9E6 0%, #FFEDD5 100%)',
              border: '2px solid #F4CE7B',
              borderRadius: '50px',
              padding: isMobile ? '8px 16px' : '10px 24px',
              marginBottom: isMobile ? '20px' : '24px'
            }}>
              <Users size={isMobile ? 16 : 20} color="#E8C068" />
              <span style={{ color: '#E8C068', fontSize: isSmallMobile ? '11px' : isMobile ? '12px' : '14px', fontWeight: '700', letterSpacing: '0.5px' }}>
                PARTNERSHIP PROGRAMS
              </span>
            </div>
            <h2 style={{
              fontSize: isSmallMobile ? '24px' : isMobile ? '32px' : '48px',
              fontWeight: '900',
              color: '#1A202C',
              marginBottom: '20px',
              letterSpacing: '-1px'
            }}>
              Find Your Perfect Partnership
            </h2>
            <p style={{
              fontSize: isMobile ? '16px' : '20px',
              color: '#64748B',
              maxWidth: '700px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              Customized solutions for healthcare providers, hospitals, enterprises, and technology partners.
            </p>
          </div>

          {/* Tab Navigation */}
          <div style={{
            display: 'flex',
            gap: isMobile ? '8px' : '12px',
            flexWrap: 'wrap',
            justifyContent: 'center',
            marginBottom: isMobile ? '32px' : '48px',
            overflowX: isMobile ? 'auto' : 'visible',
            paddingBottom: isMobile ? '8px' : '0'
          }}>
            {programs.map((program) => {
              const IconComponent = program.icon;
              const isActive = activeTab === program.id;
              return (
                <button
                  key={program.id}
                  onClick={() => setActiveTab(program.id)}
                  style={{
                    background: isActive ? program.gradient : 'white',
                    color: isActive ? 'white' : '#4A4A4A',
                    border: isActive ? 'none' : '2px solid #E8ECF0',
                    borderRadius: '50px',
                    padding: isMobile ? '10px 16px' : '12px 24px',
                    fontSize: isMobile ? '13px' : '15px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.3s ease',
                    boxShadow: isActive ? '0 4px 16px rgba(0, 0, 0, 0.15)' : 'none',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.borderColor = '#1D57D5';
                      e.currentTarget.style.color = '#1D57D5';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.borderColor = '#E8ECF0';
                      e.currentTarget.style.color = '#4A4A4A';
                    }
                  }}
                >
                  <IconComponent size={isMobile ? 16 : 18} />
                  {program.title}
                </button>
              );
            })}
          </div>

          {/* Active Program Content */}
          <div style={{
            background: 'white',
            border: '2px solid #E8ECF0',
            borderRadius: '24px',
            padding: isMobile ? '32px' : '64px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '32px' : '64px', alignItems: 'center' }}>
              {/* Left: Icon and Description */}
              <div>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: isMobile ? '80px' : '96px',
                  height: isMobile ? '80px' : '96px',
                  background: activeProgram.gradient,
                  borderRadius: '24px',
                  marginBottom: isMobile ? '20px' : '24px',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
                }}>
                  {React.createElement(activeProgram.icon, { size: isMobile ? 40 : 48, color: 'white' })}
                </div>
                <h3 style={{
                  fontSize: isMobile ? '24px' : '32px',
                  fontWeight: '800',
                  color: '#1A202C',
                  marginBottom: '16px'
                }}>
                  {activeProgram.title}
                </h3>
                <p style={{
                  fontSize: isMobile ? '16px' : '18px',
                  color: '#64748B',
                  lineHeight: '1.7',
                  marginBottom: isMobile ? '24px' : '32px'
                }}>
                  {activeProgram.description}
                </p>
                <button
                  onClick={() => navigate(activeProgram.link)}
                  style={{
                    background: activeProgram.gradient,
                    color: 'white',
                    border: 'none',
                    borderRadius: '50px',
                    padding: isMobile ? '14px 28px' : '16px 32px',
                    fontSize: isMobile ? '15px' : '16px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    width: isMobile ? '100%' : 'auto',
                    justifyContent: 'center',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.2)';
                  }}
                >
                  {activeProgram.cta}
                  <ArrowRight size={isMobile ? 16 : 18} />
                </button>
              </div>

              {/* Right: Benefits List */}
              <div>
                <h4 style={{
                  fontSize: isMobile ? '18px' : '20px',
                  fontWeight: '700',
                  color: '#1A202C',
                  marginBottom: isMobile ? '20px' : '24px'
                }}>
                  What You Get:
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '12px' : '16px' }}>
                  {activeProgram.benefits.map((benefit, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <div style={{
                        minWidth: '24px',
                        minHeight: '24px',
                        background: activeProgram.gradient,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginTop: '2px',
                        flexShrink: 0
                      }}>
                        <CheckCircle size={14} color="white" />
                      </div>
                      <span style={{
                        fontSize: isMobile ? '14px' : '16px',
                        color: '#4A4A4A',
                        lineHeight: '1.6'
                      }}>
                        {benefit}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* GEWO Technology Platform */}
      <div style={{
        background: 'linear-gradient(135deg, #1A202C 0%, #2D3748 100%)',
        padding: isMobile ? '60px 0' : '120px 0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative Background */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: isMobile ? '400px' : '800px',
          height: isMobile ? '400px' : '800px',
          background: 'radial-gradient(circle, rgba(29, 87, 213, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(60px)'
        }} />

        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '0 16px' : '0 32px', position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '40px' : '64px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(29, 87, 213, 0.2)',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(29, 87, 213, 0.3)',
              borderRadius: '50px',
              padding: isMobile ? '8px 16px' : '10px 24px',
              marginBottom: isMobile ? '20px' : '24px'
            }}>
              <Database size={isMobile ? 16 : 20} color="#1D57D5" />
              <span style={{ color: '#1D57D5', fontSize: isSmallMobile ? '11px' : isMobile ? '12px' : '14px', fontWeight: '700', letterSpacing: '0.5px' }}>
                GEWO PLATFORM
              </span>
            </div>
            <h2 style={{
              fontSize: isSmallMobile ? '24px' : isMobile ? '32px' : '48px',
              fontWeight: '900',
              color: 'white',
              marginBottom: '20px',
              letterSpacing: '-1px'
            }}>
              Enterprise Healthcare Technology
            </h2>
            <p style={{
              fontSize: isMobile ? '16px' : '20px',
              color: 'rgba(255, 255, 255, 0.8)',
              maxWidth: '700px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              Cloud-based, secure, and scalable healthcare infrastructure trusted by 2,500+ providers.
            </p>
          </div>

          {/* Platform Features Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: isMobile ? '20px' : '24px'
          }}>
            {[
              { icon: Cloud, title: 'Cloud-Based Infrastructure', desc: '99.9% uptime with auto-scaling' },
              { icon: Lock, title: 'End-to-End Encryption', desc: 'HIPAA & GDPR compliant security' },
              { icon: Zap, title: 'Real-Time Communication', desc: 'HD video, chat, and notifications' },
              { icon: Clock, title: 'Automated Workflows', desc: 'AI-powered scheduling & reminders' },
              { icon: Database, title: 'Digital Health Records', desc: 'Centralized patient data management' },
              { icon: BarChart3, title: 'Analytics Dashboard', desc: 'Real-time insights and reporting' }
            ].map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div
                  key={index}
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '20px',
                    padding: isMobile ? '24px' : '32px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.borderColor = 'rgba(29, 87, 213, 0.5)';
                    e.currentTarget.style.transform = 'translateY(-8px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <IconComponent size={isMobile ? 32 : 36} color="#1D57D5" style={{ marginBottom: '16px' }} />
                  <h4 style={{
                    fontSize: isMobile ? '16px' : '18px',
                    fontWeight: '700',
                    color: 'white',
                    marginBottom: '8px'
                  }}>
                    {feature.title}
                  </h4>
                  <p style={{
                    fontSize: isMobile ? '14px' : '15px',
                    color: 'rgba(255, 255, 255, 0.7)',
                    lineHeight: '1.6'
                  }}>
                    {feature.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Success Stories */}
      <div style={{
        background: 'white',
        padding: isMobile ? '60px 0' : '120px 0'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '0 16px' : '0 32px' }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '40px' : '64px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'linear-gradient(135deg, #FFF9E6 0%, #FFEDD5 100%)',
              border: '2px solid #F4CE7B',
              borderRadius: '50px',
              padding: isMobile ? '8px 16px' : '10px 24px',
              marginBottom: isMobile ? '20px' : '24px'
            }}>
              <Star size={isMobile ? 16 : 20} color="#E8C068" fill="#E8C068" />
              <span style={{ color: '#E8C068', fontSize: isSmallMobile ? '11px' : isMobile ? '12px' : '14px', fontWeight: '700', letterSpacing: '0.5px' }}>
                SUCCESS STORIES
              </span>
            </div>
            <h2 style={{
              fontSize: isSmallMobile ? '24px' : isMobile ? '32px' : '48px',
              fontWeight: '900',
              color: '#1A202C',
              marginBottom: '20px',
              letterSpacing: '-1px'
            }}>
              Trusted by Healthcare Leaders
            </h2>
            <p style={{
              fontSize: isMobile ? '16px' : '20px',
              color: '#64748B',
              maxWidth: '700px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              Hear from our partners who are transforming healthcare delivery with GEWO.
            </p>
          </div>

          {/* Testimonials Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: isMobile ? '20px' : '32px'
          }}>
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                style={{
                  background: 'linear-gradient(135deg, #F8FAFC 0%, white 100%)',
                  border: '2px solid #E8ECF0',
                  borderRadius: '20px',
                  padding: isMobile ? '24px' : '36px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <p style={{
                  fontSize: isMobile ? '14px' : '16px',
                  color: '#4A4A4A',
                  lineHeight: '1.7',
                  marginBottom: isMobile ? '20px' : '24px',
                  fontStyle: 'italic'
                }}>
                  "{testimonial.quote}"
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{
                    width: isMobile ? '48px' : '56px',
                    height: isMobile ? '48px' : '56px',
                    background: testimonial.gradient,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: isMobile ? '18px' : '20px',
                    fontWeight: '700',
                    color: 'white',
                    flexShrink: 0
                  }}>
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div style={{
                      fontSize: isMobile ? '15px' : '16px',
                      fontWeight: '700',
                      color: '#1A202C',
                      marginBottom: '4px'
                    }}>
                      {testimonial.author}
                    </div>
                    <div style={{ fontSize: isMobile ? '13px' : '14px', color: '#64748B' }}>
                      {testimonial.role}
                    </div>
                    <div style={{ fontSize: isMobile ? '12px' : '13px', color: '#94A3B8', marginTop: '2px' }}>
                      {testimonial.organization}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Trust & Certifications */}
      <div style={{
        background: 'linear-gradient(180deg, #F8FAFC 0%, white 100%)',
        padding: isMobile ? '48px 0' : '80px 0'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '0 16px' : '0 32px', textAlign: 'center' }}>
          <h3 style={{
            fontSize: isMobile ? '18px' : '24px',
            fontWeight: '700',
            color: '#64748B',
            marginBottom: isMobile ? '24px' : '32px',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>
            Certified & Compliant
          </h3>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: isMobile ? '24px' : '48px',
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            {[
              { icon: Shield, label: 'HIPAA Compliant', color: '#1D57D5' },
              { icon: CheckCircle, label: 'GDPR Certified', color: '#28C08A' },
              { icon: Lock, label: 'ISO 27001', color: '#9333EA' },
              { icon: Award, label: 'SOC 2 Type II', color: '#F97316' }
            ].map((cert, index) => {
              const IconComponent = cert.icon;
              return (
                <div key={index} style={{ textAlign: 'center' }}>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: isMobile ? '56px' : '64px',
                    height: isMobile ? '56px' : '64px',
                    background: `${cert.color}15`,
                    borderRadius: '16px',
                    marginBottom: '12px'
                  }}>
                    <IconComponent size={isMobile ? 28 : 32} color={cert.color} />
                  </div>
                  <div style={{
                    fontSize: isMobile ? '12px' : '14px',
                    fontWeight: '700',
                    color: '#4A4A4A'
                  }}>
                    {cert.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div style={{
        background: 'linear-gradient(135deg, #0D2C6E 0%, #1D57D5 50%, #286CFF 100%)',
        padding: isMobile ? '60px 0' : '100px 0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative Elements */}
        <div style={{
          position: 'absolute',
          top: '20%',
          left: '10%',
          width: isMobile ? '100px' : '200px',
          height: isMobile ? '100px' : '200px',
          background: 'radial-gradient(circle, rgba(244, 206, 123, 0.2) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(40px)'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '20%',
          right: '10%',
          width: isMobile ? '120px' : '250px',
          height: isMobile ? '120px' : '250px',
          background: 'radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(40px)'
        }} />

        <div style={{
          maxWidth: '900px',
          margin: '0 auto',
          padding: isMobile ? '0 16px' : '0 32px',
          textAlign: 'center',
          position: 'relative',
          zIndex: 1
        }}>
          <h2 style={{
            fontSize: isSmallMobile ? '28px' : isMobile ? '36px' : '56px',
            fontWeight: '900',
            color: 'white',
            lineHeight: '1.2',
            marginBottom: isMobile ? '20px' : '24px',
            letterSpacing: '-1px'
          }}>
            Ready to Transform<br />
            <span style={{
              background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Healthcare Delivery?
            </span>
          </h2>
          <p style={{
            fontSize: isMobile ? '16px' : '20px',
            color: 'rgba(255, 255, 255, 0.9)',
            marginBottom: isMobile ? '32px' : '48px',
            lineHeight: '1.6'
          }}>
            Join 2,500+ healthcare partners delivering exceptional care with GEWO's platform.
          </p>

          {/* CTA Buttons */}
          <div style={{
            display: 'flex',
            gap: isMobile ? '12px' : '16px',
            justifyContent: 'center',
            flexWrap: 'wrap',
            flexDirection: isMobile ? 'column' : 'row'
          }}>
            <button
              onClick={() => document.getElementById('partnership-programs').scrollIntoView({ behavior: 'smooth' })}
              style={{
                background: 'linear-gradient(135deg, #F4CE7B 0%, #E8C068 100%)',
                color: '#0D2C6E',
                border: 'none',
                borderRadius: '50px',
                padding: isMobile ? '16px 24px' : '20px 48px',
                fontSize: isMobile ? '16px' : '18px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                width: isMobile ? '100%' : 'auto',
                boxShadow: '0 8px 24px rgba(244, 206, 123, 0.4)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(244, 206, 123, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(244, 206, 123, 0.4)';
              }}
            >
              Become a Partner
              <ArrowRight size={isMobile ? 18 : 20} />
            </button>
            <button
              onClick={() => window.open('mailto:partnerships@gewohealth.com', '_blank')}
              style={{
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '50px',
                padding: isMobile ? '16px 24px' : '20px 48px',
                fontSize: isMobile ? '16px' : '18px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                width: isMobile ? '100%' : 'auto',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                e.currentTarget.style.border = '2px solid rgba(255, 255, 255, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                e.currentTarget.style.border = '2px solid rgba(255, 255, 255, 0.3)';
              }}
            >
              Contact Partnership Team
              <Mail size={isMobile ? 18 : 20} />
            </button>
          </div>

          {/* Contact Info */}
          <div style={{
            marginTop: isMobile ? '32px' : '48px',
            display: 'flex',
            gap: isMobile ? '20px' : '32px',
            justifyContent: 'center',
            flexWrap: 'wrap',
            flexDirection: isMobile ? 'column' : 'row'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: isMobile ? 'center' : 'flex-start' }}>
              <Phone size={isMobile ? 16 : 18} color="#F4CE7B" />
              <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: isMobile ? '14px' : '16px' }}>
                +91 99999 00000
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: isMobile ? 'center' : 'flex-start' }}>
              <Mail size={isMobile ? 16 : 18} color="#F4CE7B" />
              <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: isMobile ? '14px' : '16px' }}>
                partnerships@gewohealth.com
              </span>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Partners;
